import { StandTypeMismatchAlert } from "@/pages/rollshop/models/RollshopNotification";
import { Box, Card, Chip, Stack, Typography, alpha } from "@mui/material";

const typeNames: Record<string, string> = {
	E: "Edger",
	U: "Universal",
};

const TypeChip = ({
	code,
	mismatch = false,
	testId,
}: {
	code: string | null;
	mismatch?: boolean;
	testId: string;
}) => (
	<Chip
		data-test-id={testId}
		label={code ? (typeNames[code] ?? code) : "Unknown"}
		size="small"
		sx={{
			height: 20,
			fontSize: "0.7rem",
			fontWeight: 600,
			backgroundColor: (theme) =>
				mismatch
					? alpha(theme.palette.error.main, 0.15)
					: alpha(theme.palette.success.main, 0.15),
			color: (theme) =>
				mismatch ? theme.palette.error.main : theme.palette.success.dark,
		}}
	/>
);

const Field = ({
	label,
	testId,
	children,
}: {
	label: string;
	testId: string;
	children: React.ReactNode;
}) => (
	<Box data-test-id={testId} sx={{ minWidth: 0 }}>
		<Typography variant="caption" color="text.secondary" display="block">
			{label}
		</Typography>
		{children}
	</Box>
);

const RollRow = ({
	position,
	name,
	typeCode,
	standTypeCode,
	testId,
}: {
	position: string;
	name: string | null;
	typeCode: string | null;
	standTypeCode: string;
	testId: string;
}) => {
	const mismatch = typeCode !== null && typeCode !== standTypeCode;

	return (
		<Stack
			data-test-id={testId}
			direction="row"
			alignItems="center"
			justifyContent="space-between"
			sx={{
				p: 1.25,
				borderRadius: 1.5,
				border: (theme) =>
					mismatch
						? `1px solid ${alpha(theme.palette.error.main, 0.4)}`
						: `1px solid ${alpha("#000", 0.08)}`,
				backgroundColor: (theme) =>
					mismatch ? alpha(theme.palette.error.main, 0.04) : "transparent",
			}}
		>
			<Box>
				<Typography variant="caption" color="text.secondary">
					{position}
				</Typography>
				<Typography
					data-test-id={`${testId}-name`}
					variant="body2"
					fontWeight={600}
				>
					{name ?? "-"}
				</Typography>
			</Box>
			<TypeChip code={typeCode} mismatch={mismatch} testId={`${testId}-type`} />
		</Stack>
	);
};

type Props = {
	mismatch: StandTypeMismatchAlert;
};

export const StandTypeMismatchCard = ({ mismatch }: Props) => (
	<Card
		data-test-id="stand-type-mismatch-card"
		elevation={0}
		sx={{
			width: "100%",
			backgroundColor: "background.paper",
			borderRadius: 2.5,
			border: (theme) => `1px solid ${alpha(theme.palette.error.main, 0.25)}`,
			borderLeft: (theme) => `3px solid ${theme.palette.error.main}`,
		}}
	>
		<Box sx={{ p: 2 }}>
			<Typography variant="subtitle2" fontWeight={700} mb={1.5}>
				Mismatch details
			</Typography>

			<Stack direction="row" flexWrap="wrap" gap={3} mb={2}>
				<Field label="Carrier" testId="mismatch-carrier">
					<Typography variant="body2" fontWeight={600}>
						{mismatch.CarrierName}
					</Typography>
				</Field>
				<Field label="Section / Stand" testId="mismatch-section-stand">
					<Typography variant="body2" fontWeight={600}>
						{[mismatch.SectionName, mismatch.StandName]
							.filter(Boolean)
							.join(" / ") || "-"}
					</Typography>
				</Field>
				<Field label="Stand type" testId="mismatch-stand-type">
					<TypeChip
						code={mismatch.StandTypeCode}
						testId="mismatch-stand-type-chip"
					/>
				</Field>
				<Field label="Rollset" testId="mismatch-rollset">
					<Typography variant="body2" fontWeight={600}>
						{mismatch.RollsetLetter ?? "-"}
					</Typography>
				</Field>
			</Stack>

			<Stack spacing={1}>
				<RollRow
					position="Top roll"
					testId="mismatch-top-roll"
					name={mismatch.TopRollName}
					typeCode={mismatch.TopRollTypeCode}
					standTypeCode={mismatch.StandTypeCode}
				/>
				<RollRow
					position="Bottom roll"
					testId="mismatch-bottom-roll"
					name={mismatch.BottomRollName}
					typeCode={mismatch.BottomRollTypeCode}
					standTypeCode={mismatch.StandTypeCode}
				/>
			</Stack>
		</Box>
	</Card>
);
